import { defineNuxtPlugin } from '#app'

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('scroll-gradient', {
    // SSR support: return empty props as this directive only works on client
    getSSRProps() {
      return {}
    },

    mounted(el, binding) {
      // SSR 環境跳過
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return
      }

      const isRTL = getComputedStyle(el).direction === 'rtl'

      const computedStyle = window.getComputedStyle(el)
      if (computedStyle.position === 'static') {
        el.style.position = 'relative'
      }

      if (!['auto', 'scroll'].includes(computedStyle.overflow) && 
          !['auto', 'scroll'].includes(computedStyle.overflowX) && 
          !['auto', 'scroll'].includes(computedStyle.overflowY)) {
        console.error('Element with v-scroll-gradient should have overflow: "auto" or "scroll"')
      }

      // 預設配置
      const options = {
        gradientHeight: 30, // 漸層高度 (垂直方向）
        gradientWidth: 50,  // 漸層寬度（水平方向）
        topColor: 'rgba(0, 0, 0, 0)', // 漸層起始色
        bottomColor: 'rgba(0, 0, 0, 0.5)', // 漸層結束色
        threshold: 5, // 觸發漸層的滾動閾值
        direction: 'vertical', // 漸層方向：'vertical'/'horizontal'/'both'
        ...binding.value,
      }

      // 獲取當前方向的函數
      const getCurrentDirection = () => {
        // 根據螢幕方向自動判斷漸層方向
        if (options.direction !== 'both') return options.direction
        const isPortrait = typeof window.matchMedia === 'function'
          ? window.matchMedia('(orientation: portrait)').matches
          : true
        return isPortrait ? 'vertical' : 'horizontal'
      }

      // 標準化 scrollLeft (處理瀏覽器 RTL 差異)
      const normalizeScrollLeft = (scrollLeft, maxScrollX, isRTL) => {
        if (!isRTL) {
          // LTR 模式：直接使用
          return scrollLeft
        }
        
        // RTL 模式：Chrome/Firefox/Safari/Edge 已統一行為
        // - 起始位置 scrollLeft = 0 或接近 0（如 0.75, -1.5），向左滾動時變為負值（-10, -20, -100...）
        // - 取絕對值統一轉換為 LTR 等效的正值座標
        // - 注意：舊版 Firefox (< v75, 2020年前) 使用不同邏輯，此處不支援
        return Math.abs(scrollLeft)
      }

      // 更新漸層顯示狀態的輔助函數
      const setGradientVisibility = (gradient, shouldShow) => {
        if (!gradient) return
        gradient.style.display = shouldShow ? 'block' : 'none'
        gradient.style.opacity = shouldShow ? '1' : '0'
      }

      // 創建漸層元素
      const createGradientElement = (position) => {
        const gradient = document.createElement('div')
        gradient.className = `scroll-gradient-${position}`

        const baseStyles = {
          position: 'absolute',
          pointerEvents: 'none',
          transition: 'opacity 0.3s ease',
          opacity: '0',
          zIndex: '21',
          transform: 'translateZ(0)', // 強制硬體加速
          willChange: 'opacity', // 優化動畫性能
          flexShrink: 0,
        }

        if (position === 'top') {
          Object.assign(gradient.style, {
            ...baseStyles,
            width: '100%',
            left: '0',
            right: '0',
            height: `${options.gradientHeight}px`,
            background: `linear-gradient(to bottom, ${options.bottomColor}, ${options.topColor})`,
          })
        } else if (position === 'bottom') {
          Object.assign(gradient.style, {
            ...baseStyles,
            width: '100%',
            left: '0',
            right: '0',
            height: `${options.gradientHeight}px`,
            background: `linear-gradient(to top, ${options.bottomColor}, ${options.topColor})`,
          })
        } else if (position === 'left') {
          Object.assign(gradient.style, {
            ...baseStyles,
            height: '100%',
            top: '0',
            width: `${options.gradientWidth}px`,
            background: `linear-gradient(to ${isRTL ? 'left' : 'right'}, ${options.bottomColor}, ${options.topColor})`,
          })
        } else if (position === 'right') { 
          Object.assign(gradient.style, {
            ...baseStyles,
            height: '100%',
            top: '0',
            width: `${options.gradientWidth}px`,
            background: `linear-gradient(to ${isRTL ? 'right' : 'left'}, ${options.bottomColor}, ${options.topColor})`,
          })
        }

        return gradient
      }

      // 動態管理漸層元素
      let gradients = {}
      let currentDirection = null

      const createGradients = (direction) => {
        // 清理現有漸層
        Object.values(gradients).forEach(gradient => {
          if (gradient) gradient.remove()
        })
        gradients = {}

        if (direction === 'vertical' || direction === 'both') {
          gradients.top = createGradientElement('top')
          gradients.bottom = createGradientElement('bottom')
          el.prepend(gradients.top)
          el.append(gradients.bottom)
        }
        
        if (direction === 'horizontal' || direction === 'both') {
          gradients.left = createGradientElement('left')
          gradients.right = createGradientElement('right')
          el.prepend(gradients.left)
          el.append(gradients.right)
        }
      }

      // 檢查滾動位置並更新漸層
      const updateGradients = () => {
        // 確保元素可見
        if (el.offsetParent === null) return

        // 檢查方向是否改變
        const newDirection = getCurrentDirection()
        if (newDirection !== currentDirection) {
          currentDirection = newDirection
          createGradients(currentDirection)
        }
        
        // 垂直滾動處理
        if (currentDirection === 'vertical') {
          const { scrollTop, scrollHeight, clientHeight } = el
          const maxScroll = scrollHeight - clientHeight

          // 上方漸層：動態更新位置並控制顯示
          if (gradients.top) {
            gradients.top.style.top = `${scrollTop}px`
            setGradientVisibility(gradients.top, scrollTop > options.threshold)
          }

          // 下方漸層：動態更新位置並控制顯示
          if (gradients.bottom) {
            gradients.bottom.style.top = `${scrollTop + clientHeight - options.gradientHeight}px`
            setGradientVisibility(gradients.bottom, scrollTop < maxScroll - options.threshold)
          }
        }

        // 水平滾動處理
        if (currentDirection === 'horizontal') {
          const { scrollLeft, scrollWidth, clientWidth } = el
          const maxScrollX = scrollWidth - clientWidth
          const normalizedScrollLeft = normalizeScrollLeft(scrollLeft, maxScrollX, isRTL)

          // 左方漸層：動態更新位置並控制顯示
          if (gradients.left) {
            if (isRTL) {
              // RTL: 漸層在視覺右邊，使用 right 屬性定位
              gradients.left.style.right = `${normalizedScrollLeft}px`
              gradients.left.style.left = 'auto'
            } else {
              // LTR: 漸層在視覺左邊，使用 left 屬性定位
              gradients.left.style.left = `${normalizedScrollLeft}px`
              gradients.left.style.right = 'auto'
            }
            setGradientVisibility(gradients.left, normalizedScrollLeft > options.threshold)
          }

          // 右方漸層：動態更新位置並控制顯示
          if (gradients.right) {
            if (isRTL) {
              // RTL: 漸層在視覺左邊，使用 right 屬性定位（從右側計算位置）
              gradients.right.style.right = `${normalizedScrollLeft + clientWidth - options.gradientWidth}px`
              gradients.right.style.left = 'auto'
            } else {
              // LTR: 漸層在視覺右邊，使用 left 屬性定位（從左側計算位置）
              gradients.right.style.left = `${normalizedScrollLeft + clientWidth - options.gradientWidth}px`
              gradients.right.style.right = 'auto'
            }
            setGradientVisibility(gradients.right, normalizedScrollLeft < maxScrollX - options.threshold)
          }
        }
      }

      // // 處理 display: none 切換的觀察器
      // const resizeObserver = new ResizeObserver(() => {
      //   // 當元素從隱藏變為可見時，重新計算
      //   setTimeout(updateGradients, 0)
      // })

      // 監聽滾動事件
      let ticking = false
      const handleScroll = () => {
        if (!ticking) {
          requestAnimationFrame(() => {
            updateGradients()
            ticking = false
          })
          ticking = true
        }
      }

      // 追蹤 pending timeouts 以便清理
      const pendingTimeouts = []

      // 監聯螢幕方向變化
      const handleOrientationChange = () => {
        // 延遲一點讓方向變化完成
        const timeoutId = setTimeout(updateGradients, 100)
        pendingTimeouts.push(timeoutId)
      }

      // 監聽視窗調整大小事件
      const handleResize = () => {
        updateGradients()
      }

      // 監聽方向變化
      const orientationMediaQuery = typeof window.matchMedia === 'function'
        ? window.matchMedia('(orientation: portrait)')
        : null

      // 綁定事件
      el.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', handleResize)
      // resizeObserver.observe(el)
      orientationMediaQuery?.addEventListener('change', handleOrientationChange)

      // 初始設定
      currentDirection = getCurrentDirection()
      createGradients(currentDirection)
      const initialTimeoutId = setTimeout(updateGradients, 0)
      pendingTimeouts.push(initialTimeoutId)

      // 儲存清理函數和元素引用
      el._scrollGradientCleanup = () => {
        // 清理所有 pending timeouts
        pendingTimeouts.forEach(id => clearTimeout(id))
        pendingTimeouts.length = 0

        el.removeEventListener('scroll', handleScroll)
        window.removeEventListener('resize', handleResize)
        // resizeObserver.disconnect()
        orientationMediaQuery?.removeEventListener('change', handleOrientationChange)
        Object.values(gradients).forEach(gradient => {
          if (gradient) gradient.remove()
        })
      }

      // 保存 pendingTimeouts 引用供 updated hook 使用
      el._scrollGradientTimeouts = pendingTimeouts

      el._scrollGradientElements = { ...gradients }
      el._scrollGradientUpdate = updateGradients
    },

    updated(el, binding) {
      // SSR 環境跳過
      if (typeof window === 'undefined') {
        return
      }

      // 當綁定值更新時，重新檢查漸層
      if (el._scrollGradientUpdate) {
        const timeoutId = setTimeout(el._scrollGradientUpdate, 0)
        // 追蹤此 timeout 以便清理
        if (el._scrollGradientTimeouts) {
          el._scrollGradientTimeouts.push(timeoutId)
        }
      }
    },

    unmounted(el) {
      // SSR 環境跳過
      if (typeof window === 'undefined') {
        return
      }

      // 清理資源
      if (el._scrollGradientCleanup) {
        el._scrollGradientCleanup()
      }
    },
  })
})
